"use client"

import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, useFieldArray } from "react-hook-form"
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  FormField,
  FormDescription,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button";
import { Turnstile } from '@marsidev/react-turnstile'
import { useEffect, useState } from "react"
import { Link, useFetcher, useLoaderData } from "@remix-run/react"
import { capitalizeFirstLetter } from "@/lib/utils"

// Minimum 8 characters, at least one uppercase letter, one lowercase letter, one number and one special character
const passwordValidation = new RegExp(
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^.,&*-]).{8,}$/
  );

export const formSchema = z.object({
    firstName: z.string().min(1, "Name can't be empty."),
    lastName: z.string().min(1, "Name can't be empty."),
    email: z.string().min(4, "Email must be more than 4 characters.").email(),
    password: z.string().min(8, "Password have at least 8 characters.").regex(passwordValidation, "Password must be 8 characters, have at least one uppercase characters, one lowercase, one number and a special character."),

})

export default function SignupForm(){
    const fetcher = useFetcher({key: "signup"});
    const [captchaToken, setCaptchaToken] = useState('')

    const site_key = useLoaderData<string>()

    const [passwordVisibility, setPasswordVisibility] = useState(false);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: "",
            password: "",
            firstName: "",
            lastName: ""
        },
        mode: "onSubmit"
    })

    const toggleVisibility = () => {
        setPasswordVisibility(!passwordVisibility);
    }

    const onSubmit = (values: z.infer<typeof formSchema>) => {
        const formData = new FormData();

        if(captchaToken.length === 0) return;

        formData.append('email', values.email);
        formData.append('password', values.password);
        formData.append('firstName', capitalizeFirstLetter(values.firstName));
        formData.append('lastName', capitalizeFirstLetter(values.lastName));
        formData.append('captcha', captchaToken);

        fetcher.submit(formData, {method: 'post', action: './'});
    }

    const {watch} = form;

    const email = watch("email")
    const password = watch("password")
    const firstName = watch("firstName")
    const lastName = watch("lastName")

    const isFormFilled = email || password || firstName || lastName;

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="w-[360px] max-w-[360px] max-[360px]:w-full flex flex-col gap-2 mt-0">

                <h1 className="text-center self-center text-3xl font-semibold mb-6 lg:block hidden">Let's get started.</h1>
                <h1 className="text-3xl font-semibold mb-6 block lg:hidden text-center">Great! Let's start with the basics.</h1>

                
                <FormField
                    name="firstName"
                    control={form.control}
                    render={({field}) => (
                        <FormItem>
                            <FormLabel>First name</FormLabel>
                                <FormControl>
                                    <Input {...field} placeholder="Enter your first name" autoComplete="name" autoCapitalize="on"/> 
                                </FormControl>
                                <FormMessage/>
                        </FormItem>
                    )}
                />

                <FormField
                    name="lastName"
                    control={form.control}
                    render={({field}) => (
                        <FormItem>
                            <FormLabel>Last name</FormLabel>
                                <FormControl>
                                    <Input {...field} placeholder="Enter your last name" autoComplete="name" autoCapitalize="on"/> 
                                </FormControl>
                                <FormMessage/>
                        </FormItem>
                    )}
                />

                <FormField
                    name="email"
                    control={form.control}
                    render={({field}) => (
                        <FormItem>
                            <FormLabel>Email</FormLabel>
                                <FormControl>
                                    <Input {...field} placeholder="Enter your email address" autoComplete="email"/> 
                                </FormControl>
                                <FormMessage/>
                        </FormItem>
                    )}
                />

                <FormField
                    name="password"
                    control={form.control}
                    render={({field}) => (
                        <FormItem>
                            <FormLabel>Password</FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <Input {...field} type={passwordVisibility ? 'text' : 'password'} id="passwordBox" placeholder="Enter a password" autoComplete="on"/> 
                                        <span className="absolute right-4 top-1/2 transform -translate-y-1/2" onClick={toggleVisibility}>
                                            {passwordVisibility ? 
                                                <img src="eye.svg" className="filter brightness-0 invert-[20%]"/> 
                                                : 
                                                <img src="eye-off.svg" className="filter brightness-0 invert-[20%]"/>
                                            }
                                        </span>
                                    </div>
                                   
                                </FormControl>
                                {/* <FormDescription className="text-sm text-[#525353]">(Password min 8 characters.)</FormDescription> */}
                                <FormMessage/>
                        </FormItem>
                    )}
                />

                <p className="text-sm text-[#525353] mb-4">By continuing, you agree to our website <a className="hover:underline font-semibold" href="https://morby.xyz/terms">terms</a> and acknowledge our <a className="hover:underline font-semibold" href="https://morby.xyz/privacy-policy">privacy policy.</a></p>

                <Turnstile
                    siteKey={site_key}
                    onSuccess={(token) => {
                        setCaptchaToken(token)
                    }}
                    options={{
                        size: 'flexible'
                    }}
                />

                <Button
                    type="submit"
                    disabled={fetcher.state != 'idle' || !isFormFilled || captchaToken.length === 0}
                    className={`w-full bg-[#7F56D9] text-white hover:bg-[#6941C6]`}>

                    {fetcher.state != 'idle' && <div className="w-4 h-4 border-4 border-t-4 border-t-white border-[#7F56D9] rounded-full animate-spin mr-2" />}
                    Get Started
                </Button>


            </form>
        </Form>
    )
}

